import * as React from "react";
import { Subject, Subscription } from 'rxjs';
import { isNullOrUndefined } from "../utils";
import { ValidatorFieldDef, ValidatorField, ValidatorFields } from "../validators";

interface FormField {
    label: string;
    placeholder?: string;
    note?: string;
    defaultValue?: any;
}


export interface RxFormFieldDef extends FormField, ValidatorFieldDef {
}

export interface RxFormField extends FormField, ValidatorField {
    name: string;
    id: string;
    disabled: boolean;
    touched: boolean;
    error?: string;
    valid: boolean;
    onChange?: (event: React.ChangeEvent) => void;
    onBlur?: (event: React.FocusEvent) => void;
    setValue?: (value: any) => void;
}

export interface RxFormFieldDefs {
    [key: string]: RxFormFieldDef;
}

export interface RxFormFields {
    [key: string]: RxFormField;
}

export interface RxFormProps {
    submitting: boolean;
    handleSubmit: (e: React.FormEvent, disableValidation?: boolean) => void;
    errors: { [key: string]: string };
    globalError?: string;
    fields: RxFormFields;
    reset: () => void;
}



export interface Props {
    formName: string;
    defaultValues?: { [key: string]: any };
    onSubmit: (data: { [key: string]: any }, success: () => void, failure: (errors: { [key: string]: string | string[] }) => void) => void;
    disableValidation?: boolean;
    onValidated?: (fields: RxFormFields) => void;
}

interface State {
    fields: RxFormFields;
    submitting: boolean;
    errors: { [key: string]: string };
    globalError?: string;
}

interface RxFormValidationResult {
    fields: RxFormFields;
    globalError?: string;
    errors: { [key: string]: string }
    isValid: boolean;
}


export const withRxForm = (fields: RxFormFieldDefs, validate?: (fields: ValidatorFields) => { [key: string]: string }) => {

    return <T, TP extends object>(WrappedComponent: React.ComponentType<TP & RxFormProps>): React.ComponentType<TP & Props> => {
        return class Component extends React.Component<TP & Props, State> {
            constructor(props: TP & Props) {
                super(props);
                this.state = {
                    submitting: false,
                    errors: {},
                    fields: this.buildFields(this.props.formName, fields, this.props.defaultValues),
                }
            };

            private valueChanged$: Subject<RxFormField> = new Subject();
            private valueChangedSubscr: Subscription;
            private validateValue$: Subject<RxFormField> = new Subject();
            private validateValueSubscr: Subscription;
            private validationResult$: Subject<RxFormValidationResult> = new Subject();
            private validationResultSubscr: Subscription;

            private buildFields = (formName: string, fields: RxFormFieldDefs, defaultValues: { [key: string]: any }) => {
                let result: RxFormFields = Object.assign({});

                for (const name in fields) {
                    const f = fields[name];
                    const defValue = defaultValues !== undefined ? isNullOrUndefined(defaultValues[name]) ? f.defaultValue : defaultValues[name] : f.defaultValue;
                    const disabled = false;// disabledFields && !isNullOrUndefined(disabledFields[name]) ? true : false;

                    result[name] = Object.assign({}, {
                        name: name,
                        id: formName + "-" + name,
                        label: f.label,
                        note: f.note,
                        placeholder: f.placeholder,
                        defaultValue: defValue,
                        value: defValue,
                        disabled: disabled,
                        touched: false,
                        valid: true,
                        onChange: (event: any) => this.fieldOnChange(event, name),
                        onBlur: (event: any) => this.fieldOnBlur(event, name),
                        setValue: (value: any) => this.fieldSetValue(value, name),

                    });

                }

                return result;
            }

            private fieldSetValue = (value: any, fieldName: string) => {

                const field = Object.assign({}, this.state.fields[fieldName], { value: value });

                this.valueChanged$.next(field);
            }

            private fieldOnChange = (event: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
                let value: any;

                if (event.target !== undefined) {
                    if (event.target.files === undefined || event.target.files === null) {
                        value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
                    } else {
                        value = event.target.files;
                    }
                }

                const field = Object.assign({}, this.state.fields[fieldName], { value: value });

                this.valueChanged$.next(field);
            };

            private fieldOnBlur = (event: React.FocusEvent, fieldName: string) => {
                const field = Object.assign({}, this.state.fields[fieldName], { touched: true });
                //console.log(`fieldOnBlur [${fieldName}]: `, field);
                this.validateValue$.next(field);
            };

            private setFieldsTouched = (): RxFormFields => {
                const fields = { ...this.state.fields };
                for (const k in fields) {
                    fields[k].touched = true;
                }

                return fields;
            };

            private submitting: boolean = false;

            handleSubmit = (e: React.FormEvent, disableValidation: boolean = false) => {
                e.preventDefault();

                if (this.submitting)
                    return;

                this.submitting = true;
                this.setState({ submitting: true });
                const fields = this.setFieldsTouched();

                const validationResult = this.validateFields(fields, disableValidation);

                if (validationResult.isValid === false) {
                    this.submitting = false;
                    this.validationResult$.next(validationResult);
                } else {
                    this.validationResult$.next(validationResult);
                    const data: { [key: string]: any } = {};
                    Object.keys(fields).forEach(k => data[k] = fields[k].value);
                    this.props.onSubmit(data,
                        () => {
                            this.submitting = false;
                            this.setState({ submitting: false });
                        },
                        (errors: { [key: string]: string }) => {
                            const res = this.validateFieldsErrors(fields, errors);
                            this.submitting = false;
                            this.validationResult$.next(res);
                        });
                }


                // if (!this.isValid)
                //     return;

                // const sendData = { ...this.values };
                // if (this.props.onSubmitStarted) {
                //     this.props.onSubmitStarted();
                // }
                // const submitFunc = this.props.onSubmit(sendData);
                // if (isPromise(submitFunc)) {
                //     this.handleSubmitPromise(submitFunc);
                // }
            }
            /*
                    private setValidationResult = (result: ValidationResult) => {
                        
                                    let error = errors["_error"];
                                    let errorsCount = 0;
                                    for (const f of Object.keys(this.state.fields)) {
                                        this.state.fields[f].error = errors[f];
                                        this.state.fields[f].valid = this.state.fields[f].error ? false : true;
                                        if (this.state.fields[f].valid === false) {
                                            errorsCount++;
                                        }
                                    }
                                    this.isValid = (errorsCount == 0);
                                    this.setState({
                                        error: error,
                                        valid: (errorsCount == 0),
                                        fields: this.state.fields
                                    });
                    }*/

            private validateFields = (fields: RxFormFields, disableValidation: boolean): RxFormValidationResult => {

                const withoutValidation = disableValidation === true && this.props.disableValidation === true;

                let errors: { [key: string]: string } = {};
                if (validate && withoutValidation === false) {
                    const err = validate(fields);
                    if (err && Object.keys(err).length !== 0)
                        errors = Object.assign(errors, err);
                }

                return this.validateFieldsErrors(fields, errors);
            };

            private validateFieldsErrors = (fields: RxFormFields, errors: { [key: string]: string }): RxFormValidationResult => {

                let globalError = errors["_error"] || errors[""];
                let errorsCount = 0;
                for (const fn in fields) {
                    fields[fn].error = errors[fn];
                    fields[fn].valid = fields[fn].error ? false : true;
                    if (fields[fn].valid === false) {
                        errorsCount++;
                    }
                }

                return {
                    fields: fields,
                    errors: errors,
                    globalError: globalError,
                    isValid: (globalError !== undefined || errorsCount == 0)
                }
            }


            componentDidMount() {
                this.valueChangedSubscr = this.valueChanged$
                    //.debounceTime(500)
                    .subscribe((field) => this.validateValue$.next(field));

                this.validateValueSubscr = this.validateValue$
                    .subscribe((field) => {
                        const fields = Object.assign({}, this.state.fields, { [field.name]: field });
                        const result = this.validateFields(fields, this.props.disableValidation === true);
                        this.validationResult$.next(result);
                    });

                this.validationResultSubscr = this.validationResult$
                    .subscribe((result) => {

                        if (this.props.onValidated) {
                            this.props.onValidated(result.fields);
                        }

                        this.setState({
                            fields: result.fields,
                            globalError: result.globalError,
                            errors: result.errors,
                            submitting: this.submitting
                        });
                    });
            }

            componentWillUnmount() {

                if (this.valueChangedSubscr !== undefined) {
                    this.valueChangedSubscr.unsubscribe();
                    this.valueChangedSubscr = undefined;
                }

                if (this.validateValueSubscr !== undefined) {
                    this.validateValueSubscr.unsubscribe();
                    this.validateValueSubscr = undefined;
                }

                if (this.validationResultSubscr !== undefined) {
                    this.validationResultSubscr.unsubscribe();
                    this.validationResultSubscr = undefined;
                }
            }


            reset = () => {
                this.setState({
                    submitting: false,
                    errors: {},
                    fields: this.buildFields(this.props.formName, fields, this.props.defaultValues),
                    globalError: undefined,
                });
            }

            render() {

                const rxProps: RxFormProps = {
                    handleSubmit: this.handleSubmit,
                    submitting: this.state.submitting,
                    errors: this.state.errors,
                    globalError: this.state.globalError,
                    fields: this.state.fields,
                    reset: this.reset,

                };

                return React.createElement(WrappedComponent, Object.assign({}, this.props as TP, rxProps));
            }

        }
    }
}
